import React from 'react';

// Actions
import { updatePassword } from '@/redux/modules/auth';

// Components
import PasswordCriteria from '@/views/components/errors/PasswordCriteria';
import { FormInput } from '@/views/components/forms';
import { PrimaryButton } from '@/views/components/buttons';

// Components (Legacy)
import LegacySecurityPassword from '@/views/containers/settings/LegacySecurityPassword';
import { FormattedMessageV2 } from '@/views/components/i18n';

// Hooks
import useThemeVersion from '@/hooks/useThemeVersion';

// Libraries
import * as Yup from 'yup';
import { connect } from 'react-redux';
import { Form, FormikProps, withFormik } from 'formik';

// Utils
import notification from '@/utils/notification';
import { formatMessage } from '@/utils/i18n';
import { passwordIsValid } from '@/utils/auth';

type DispatchProps = {
  updatePassword: (current: string, password: string, confirm: string) => Promise<void>;
};

type FormProps = {
  current: string;
  password: string;
  confirm: string;
};

type Props = DispatchProps & FormikProps<FormProps> & {};

// TODO: Remove these types (Legacy Props and WrapperProps) when the legacy version is removed
type LegacyProps = Props & {
  onSave: (current: string, password: string, confirm: string) => Promise<void>;
};

type WrapperProps = Props & LegacyProps;

const SecurityPasswordV2 = (props: Props) => {
  const { errors, initialValues, isSubmitting, values } = props;

  const canSubmit = () => {
    const isDirty = values !== initialValues;
    const hasErrors = Object.keys(errors).length > 0;

    return !hasErrors && isDirty && !isSubmitting;
  };

  return (
    <div className="settings-security-password">
      <FormattedMessageV2 className="settings-security-password-title" toLocalize="Settings.Security.Password.Title" />
      <Form>
        <div className="password-form-fields">
          <FormInput className="form-input-v2" name="current" type="password" label={formatMessage('Settings.Security.Password.Fields.Current')} required />
          <FormInput className="form-input-v2" name="password" type="password" label={formatMessage('Settings.Security.Password.Fields.Password')} required />
          <FormInput className="form-input-v2" name="confirm" type="password" label={formatMessage('Settings.Security.Password.Fields.Confirm')} required />
          <PasswordCriteria password={values.password} confirm={values.confirm} />
        </div>
        <PrimaryButton className="primary-v2" disabled={!canSubmit()} type="submit">
          {formatMessage('Common.Save')}
        </PrimaryButton>
      </Form>
    </div>
  );
};

const SettingsSecuritySchema = Yup.object().shape({
  current: Yup.string().required(),
  // The test message doesn't need to be translated - won't be shown anywhere.
  password: Yup.string().required().test('test-password', 'Password criteria unmet', passwordIsValid),
  confirm: Yup.string()
    .required()
    .oneOf([Yup.ref('password'), null]),
});

const SecurityPassword: React.FC<WrapperProps> = props => {
  const { isLightVersion } = useThemeVersion();
  if (isLightVersion) {
    return <SecurityPasswordV2 {...props} />;
  }

  return <LegacySecurityPassword {...props} />;
};

const FormikWrapper = withFormik<WrapperProps, FormProps>({
  validationSchema: SettingsSecuritySchema,
  mapPropsToValues: props => ({ current: '', password: '', confirm: '' }),
  handleSubmit: ({ current, password, confirm }, { props, setSubmitting, resetForm }) => {
    props
      .updatePassword(current, password, confirm)
      .then(() => {
        notification.success(formatMessage('Settings.Security.Password.Success'));
        resetForm();
      })
      .finally(() => {
        setSubmitting(false);
      });
  },
})(SecurityPassword);

export default connect(null, {
  updatePassword,
})(FormikWrapper) as any;
